/**
 * Permission Service - 중앙화된 권한 관리
 * 
 * Firestore `admin/tiers/items`에서 요금제 정보를 로드하고
 * 기능별 권한 체크와 거부 메시지를 관리합니다.
 */

import { collection, getDocs } from 'firebase/firestore';
import { Fire, getFunc } from './FireClient';
import { Tier, DEFAULT_TIERS } from '../models/Tier';
import { Workspace } from '../models/Workspace';

interface FreeWorkspaceCheckResult {
    canCreate: boolean;
    reason?: string;
    currentFreeCount?: number;
    maxFreeWorkspaces?: number;
}

interface PermissionResult {
    allowed: boolean;
    message?: string;       // 거부 시 표시할 메시지
    upgradeRequired?: string; // 필요한 최소 tier
}

class Permission {
    // 캐시된 Tier 정보
    private static tiers: Map<string, Tier> = new Map();
    private static tierOrder: string[] = [];  // displayOrder 기준 정렬
    private static initialized = false;

    /**
     * 앱 시작 시 Tier 정보 로드
     */
    static async initialize(): Promise<void> {
        if (this.initialized) return;

        try {
            const snapshot = await getDocs(collection(Fire.store, 'admin/tiers/items'));

            if (snapshot.empty) {
                // Firestore에 Tier가 없으면 기본값 사용
                console.debug('[Permission] No tiers found in Firestore, using defaults');
                DEFAULT_TIERS.forEach(tier => {
                    this.tiers.set(tier.id, tier as Tier);
                });
            } else {
                snapshot.docs.forEach(doc => {
                    const tier = { id: doc.id, ...doc.data() } as Tier;
                    this.tiers.set(tier.id, tier);
                });
            }

            // displayOrder 기준 정렬
            this.tierOrder = Array.from(this.tiers.values())
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map(t => t.id);

            this.initialized = true;

        } catch (error) {
            console.error('[Permission] Failed to load tiers:', error);
            // 에러 시 기본값 사용
            DEFAULT_TIERS.forEach(tier => {
                this.tiers.set(tier.id, tier as Tier);
            });
            this.tierOrder = DEFAULT_TIERS.map(t => t.id);
            this.initialized = true;
        }
    }

    /**
     * Tier 정보 가져오기
     */
    static getTier(tierId: string): Tier | undefined {
        return this.tiers.get(tierId);
    }

    /**
     * 모든 Tier 목록 (displayOrder 순)
     */
    static getAllTiers(): Tier[] {
        return this.tierOrder.map(id => this.tiers.get(id)!).filter(Boolean);
    }

    /**
     * Tier 레벨 비교 (displayOrder 기준)
     */
    static isTierAtLeast(currentTier: string | undefined, minimumTier: string): boolean {
        if (!currentTier) return false;

        const currentIndex = this.tierOrder.indexOf(currentTier);
        const minimumIndex = this.tierOrder.indexOf(minimumTier);

        if (currentIndex === -1 || minimumIndex === -1) {
            console.warn('[Permission] Unknown tier:', currentTier, 'or', minimumTier);
            return false;
        }

        return currentIndex >= minimumIndex;
    }

    // ================================================================
    // 기능별 권한 체크
    // ================================================================

    /**
     * 통화 권한 체크 (BASIC 이상)
     */
    static canMakeCall(workspace: Workspace | undefined): PermissionResult {
        if (!workspace) {
            return { allowed: false, message: 'ワークスペースが見つかりません' };
        }

        if (this.isTierAtLeast(workspace.tier, 'BASIC')) {
            return { allowed: true };
        }

        return {
            allowed: false,
            message: '音声通話はBASIC以上のプランでご利用いただけます',
            upgradeRequired: 'BASIC',
        };
    }

    /**
     * 파일 업로드 권한 체크 (용량 확인)
     */
    static canUploadFile(workspace: Workspace | undefined, fileSizeBytes: number = 0): PermissionResult {
        if (!workspace) {
            return { allowed: false, message: 'ワークスペースが見つかりません' };
        }

        const tier = this.tiers.get(workspace.tier || 'FREE');
        if (!tier) {
            return { allowed: true }; // Tier 정보 없으면 허용
        }

        const usedBytes = workspace.overview?.usage?.storageBytes || 0;
        const limitBytes = tier.limits.storageBytes;

        // 무제한(0)이면 허용
        if (limitBytes === 0) {
            return { allowed: true };
        }

        if (usedBytes + fileSizeBytes > limitBytes) {
            return {
                allowed: false,
                message: 'ストレージ容量の上限に達しました。プランをアップグレードしてください。',
                upgradeRequired: this.getNextTier(workspace.tier)?.id,
            };
        }

        return { allowed: true };
    }

    /**
     * 방 생성 권한 체크 (채널 수 확인)
     */
    static canCreateRoom(workspace: Workspace | undefined): PermissionResult {
        if (!workspace) {
            return { allowed: false, message: 'ワークスペースが見つかりません' };
        }

        const tier = this.tiers.get(workspace.tier || 'FREE');
        if (!tier) {
            return { allowed: true };
        }

        const currentCount = workspace.overview?.usage?.channelCount || 0;
        const limit = tier.limits.channelCount;

        // 무제한(0)이면 허용
        if (limit === 0) {
            return { allowed: true };
        }

        if (currentCount >= limit) {
            return {
                allowed: false,
                message: 'チャンネル数の上限に達しました。プランをアップグレードしてください。',
                upgradeRequired: this.getNextTier(workspace.tier)?.id,
            };
        }

        return { allowed: true };
    }

    /**
     * 멤버 초대 권한 체크 (멤버 수 확인)
     */
    static canInviteMember(workspace: Workspace | undefined): PermissionResult {
        if (!workspace) {
            return { allowed: false, message: 'ワークスペースが見つかりません' };
        }

        const tier = this.tiers.get(workspace.tier || 'FREE');
        if (!tier) {
            return { allowed: true };
        }

        const currentCount = workspace.overview?.usage?.memberCount || 0;
        const limit = tier.limits.memberCount;

        // 무제한(0)이면 허용
        if (limit === 0) {
            return { allowed: true };
        }

        if (currentCount >= limit) {
            return {
                allowed: false,
                message: 'メンバー数の上限に達しました。プランをアップグレードしてください。',
                upgradeRequired: this.getNextTier(workspace.tier)?.id,
            };
        }

        return { allowed: true };
    }

    /**
     * API 접근 권한 체크
     */
    static canUseAPI(workspace: Workspace | undefined): PermissionResult {
        if (!workspace) {
            return { allowed: false, message: 'ワークスペースが見つかりません' };
        }

        const tier = this.tiers.get(workspace.tier || 'FREE');
        if (!tier) {
            return { allowed: true };
        }

        if (tier.features.apiAccess) {
            return { allowed: true };
        }

        return {
            allowed: false,
            message: 'API機能はBASIC以上のプランでご利用いただけます',
            upgradeRequired: 'BASIC',
        };
    }

    /**
     * 분석 기능 권한 체크
     */
    static canUseAnalytics(workspace: Workspace | undefined): PermissionResult {
        if (!workspace) {
            return { allowed: false, message: 'ワークスペースが見つかりません' };
        }

        const tier = this.tiers.get(workspace.tier || 'FREE');
        if (!tier) {
            return { allowed: true };
        }

        if (tier.features.analytics) {
            return { allowed: true };
        }

        return {
            allowed: false,
            message: '分析機能はBASIC以上のプランでご利用いただけます',
            upgradeRequired: 'BASIC',
        };
    }

    /**
     * 다음 상위 Tier 가져오기
     */
    private static getNextTier(currentTier: string | undefined): Tier | undefined {
        if (!currentTier) return this.tiers.get(this.tierOrder[0]);

        const currentIndex = this.tierOrder.indexOf(currentTier);
        if (currentIndex === -1 || currentIndex >= this.tierOrder.length - 1) {
            return undefined;
        }

        return this.tiers.get(this.tierOrder[currentIndex + 1]);
    }

    // ================================================================
    // 무료 워크스페이스 생성 제한
    // ================================================================

    /**
     * 무료 워크스페이스 생성 가능 여부 확인
     * 백엔드 Cloud Function 호출 (비용: 1 read + 소유 워크스페이스 수만큼 read)
     */
    static async canCreateFreeWorkspace(): Promise<FreeWorkspaceCheckResult> {
        try {
            const func = getFunc('canCreateFreeWorkspace');
            const result = await func({});
            return result.data as FreeWorkspaceCheckResult;
        } catch (error) {
            console.error('[Permission] Failed to check free workspace limit:', error);
            // 에러 시 기본적으로 생성 허용 (graceful degradation)
            return { canCreate: true, reason: 'Error checking limit' };
        }
    }
}

// 짧은 별칭 export
export const Perm = Permission;
export type { PermissionResult, FreeWorkspaceCheckResult };
